import React from "react";
import { t } from "i18next";
import {
  Box,
  Button,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Checkbox,
  useColorModeValue,
} from "@chakra-ui/react/dist/chakra-ui-react.cjs";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import DeleteButton from "components/Buttons/DeleteButton";
import { documentTypeName } from "utils";
import { toast } from "react-toastify";
import TableFixedHeader from "components/Tables/TableFixedHeader";
import { MAX_DOCUMENT_CAN_SELECT } from "constants";


const DocumentsTable = ({
  listDocument,
  params,
  onRequestDelete,
  onRequestDetail,
  isBulkAsign,
  selectedDocuments,
  setSelectedDocuments,
}) => {
  const handleToggleCheckboxAll = (e) => {
    const checked = e.target.checked;

    if (!listDocument.length) return;

    if (checked) {
      const prev = selectedDocuments || [];
      const merged = Array.from(new Set([...prev, ...listDocument]));
      if (merged.length > MAX_DOCUMENT_CAN_SELECT) {
        setSelectedDocuments(merged.slice(0, MAX_DOCUMENT_CAN_SELECT));

        toast.error(t("pages.bulk_assign.error_max_documents"));
        return;
      }
      setSelectedDocuments(merged);
    } else {
      const next = selectedDocuments.filter(
        (selectedDoc) => !listDocument.find((d) => d.id === selectedDoc.id)
      );
      setSelectedDocuments(next);
    }
  };

  const handleToggleCheckbox = (e, selectedDoc) => {
    const { checked } = e.target;

    if (checked) {
      const prev = selectedDocuments || [];
      if (prev.find((doc) => doc.id === selectedDoc.id)) return;
      const next = [...prev, selectedDoc];
      if (next.length > MAX_DOCUMENT_CAN_SELECT) {
        toast.error(t("pages.bulk_assign.error_max_documents"));
        return;
      }
      setSelectedDocuments(next);
    } else {
      setSelectedDocuments(
        selectedDocuments.filter((doc) => doc.id !== selectedDoc.id)
      );
    }
  };

  const columns = [
    ...(!isBulkAsign
      ? [
          {
            key: "index",
            header: "",
            width: "50px",
            render: (item, index) =>
              index + 1 + params.limit * (params.page - 1),
            cellProps: { textAlign: "center" },
          },
        ]
      : []),
    {
      key: "type",
      header: t("pages.document.type"),
      width: "6%",
      render: (item) => documentTypeName(item.type),
      cellProps: { whiteSpace: "pre-line", wordBreak: "break-all" },
    },
    {
      key: "fiscal_year_jp",
      header: t("pages.document.list.search_form.fiscal_year"),
      width: "100px",
      render: (item) => {
        if (!item.fiscal_year_jp) return "";
        // Format from "YYYY-MM" to "YYYY年M月"
        const [year, month] = item.fiscal_year_jp.split("-");
        if (year && month) {
          const monthNum = parseInt(month, 10);
          return `${year}年${monthNum}月`;
        }
        return item.fiscal_year_jp;
      },
      cellProps: { whiteSpace: "pre-line", wordBreak: "break-all" },
    },
    {
      key: "targetGroup",
      header: t("pages.document.list.table.target_group_name"),
      width: "15%",
      render: (item) => (
        <Text noOfLines={1} fontSize="14px">
          {item.targetGroup?.name ?? ""}
        </Text>
      ),
      cellProps: { whiteSpace: "pre-line", wordBreak: "break-all" },
    },
    {
      key: "course_code",
      header: t("pages.document.list.table.course_id"),
      width: "15%",
      render: (item) => (
        <Text noOfLines={1} fontSize="14px">
          {item.course_code}
        </Text>
      ),
      cellProps: { whiteSpace: "pre-line", wordBreak: "break-all" },
    },
    {
      key: "name",
      header: t("pages.document.list.table.document_name"),
      width: "auto",
      render: (item) => (
        <Text noOfLines={1} fontSize="14px">
          {item.name}
        </Text>
      ),
      cellProps: { whiteSpace: "pre-line", wordBreak: "break-all" },
    },
    {
      key: "description",
      header: t("pages.document.list.table.document_detail"),
      width: isBulkAsign ? "20%" : "10%",
      render: (item) => (
        <Text noOfLines={1} fontSize="14px">
          {item.description}
        </Text>
      ),
      cellProps: { whiteSpace: "pre-line", wordBreak: "break-all" },
    },
    {
      key: "actions",
      header: isBulkAsign ? (
        <Box textAlign="right">
          <Text fontSize="14px" mb="4px">
            {t("pages.document.list.table.select")}
          </Text>
          <Checkbox
            isChecked={
              ((listDocument || []).length > 0 &&
                (listDocument || []).every((d) =>
                  selectedDocuments.find((sd) => sd.id === d.id)
                )) ||
              selectedDocuments.length > MAX_DOCUMENT_CAN_SELECT - 1
            }
            onChange={handleToggleCheckboxAll}
          />
        </Box>
      ) : (
        t("pages.document.list.table.to_detail")
      ),
      width: isBulkAsign ? "50px" : "130px",
      render: (item) =>
        isBulkAsign ? (
          <Checkbox
            isChecked={!!selectedDocuments.find((sd) => sd.id === item.id)}
            onChange={(e) => handleToggleCheckbox(e, item)}
          />
        ) : (
          <>
            {item.courses?.length < 1 && (
              <DeleteButton
                text={t("pages.document.list.table.delete_button")}
                size={"xs"}
                styles={{ fontSize: "14px" }}
                marginRight={"15px"}
                onClick={() => onRequestDelete(item)}
              />
            )}
            <Button
              onClick={() => onRequestDetail(item)}
              color="white"
              background="#007bff"
              fontSize="14px"
              _hover={{ background: "#007bff99" }}
              size={"xs"}
            >
              {t("pages.document.list.table.to_detail_button")}
            </Button>
          </>
        ),
      cellProps: {
        textAlign: "right",
      },
    },
  ];

  return (
    <TableFixedHeader
      data={listDocument || []}
      columns={columns}
      maxHeight="100%"
      size="xs"
      variant="simple"
    >
      {listDocument && !listDocument.length && (
        <Box textAlign="center" pt="20px" color="gray.400" fontSize="14px">
          {t("messages.common_s003")}
        </Box>
      )}
    </TableFixedHeader>
  );
};

export default DocumentsTable;
